import { useGetPlatformSettings } from '@myrentalmate/api-client';
import { Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import { okData } from '../../lib/apiData';

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack
      direction="row"
      useFlexGap
      sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}
    >
      <Typography color="text.secondary">{label}</Typography>
      <Typography component="div" sx={{ overflowWrap: 'anywhere' }}>
        {children}
      </Typography>
    </Stack>
  );
}

/** What the app is running with. Read-only: these are set at deploy time, not here. */
export function PlatformSettingsPage() {
  const settings = okData(useGetPlatformSettings().data);
  if (!settings) return <CircularProgress aria-label="Loading" />;
  return (
    <Stack spacing={3} sx={{ maxWidth: 640 }}>
      <Typography color="text.secondary">
        How the app is set up. These are changed at deployment, not on this page.
      </Typography>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          App
        </Typography>
        <Stack spacing={1}>
          <Row label="Name">{settings.app_name}</Row>
          <Row label="Address">{settings.app_url}</Row>
          <Row label="Environment">{settings.environment}</Row>
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Email
        </Typography>
        <Stack spacing={1}>
          <Row label="Sending">
            <Chip
              size="small"
              label={settings.email_configured ? 'Set up' : 'Not set up'}
              color={settings.email_configured ? 'success' : 'warning'}
            />
          </Row>
          <Row label="Sent from">{settings.email_from}</Row>
          <Row label="Support address">{settings.support_email}</Row>
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Platform Admins
        </Typography>
        <Stack spacing={1}>
          {settings.platform_admins.map((admin) => (
            <Row key={admin.id} label={admin.display_name || 'No name yet'}>
              {admin.email}{' '}
              <Chip
                size="small"
                label={admin.mfa_enabled ? 'Two-factor on' : 'Two-factor off'}
                color={admin.mfa_enabled ? 'success' : 'default'}
              />
            </Row>
          ))}
        </Stack>
      </Paper>
    </Stack>
  );
}
